import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Tooltip, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { getDir } from "orion-components/i18n/Config/selector";
import { getTranslation } from "orion-components/i18n";

const AddPresetDialog = ({ open, onClose, onSave, isSaving, presets, isPresetValid, container }) => {
	const [selectedPresetValue, setSelectedPresetValue] = useState("");
	const dir = useSelector(state => getDir(state));
	const dialogContainer = useMemo(() => {
		const fullscreenElement = document.fullscreenElement;
		const usePlayerContainer =
			container &&
			fullscreenElement &&
			(fullscreenElement === container || fullscreenElement.contains(container));

		return usePlayerContainer ? () => container : undefined;
	}, [container, open]);

	useEffect(() => {
		if (open) {
			setSelectedPresetValue("");
		}
	}, [open]);

	const presetSlots = useMemo(() => {
		const slotMap = new Map();

		presets.forEach(preset => {
			const parsedValue = Number.parseInt(preset?.value, 10);
			if (!Number.isInteger(parsedValue) || parsedValue < 1 || parsedValue > 20) return;

			slotMap.set(parsedValue, {
				name: preset?.label || preset?.name || null
			});
		});

		return Array.from({ length: 20 }, (_, index) => {
			const slotNumber = index + 1;
			const existingPreset = slotMap.get(slotNumber);

			return {
				value: String(slotNumber),
				number: slotNumber,
				hasPreset: Boolean(existingPreset),
				name: existingPreset?.name || ""
			};
		});
	}, [presets]);

	const canSave = isPresetValid && selectedPresetValue !== "" && !isSaving;

	const handleSubmit = () => {
		if (!selectedPresetValue) return;
		onSave(selectedPresetValue);
	};

	const handleDialogClose = () => {
		if (!isSaving) {
			onClose();
		}
	};

	return (
		<Dialog
			open={open}
			onClose={handleDialogClose}
			container={dialogContainer}
			PaperProps={{
				style: {
					backgroundColor: "#212327",
					padding: "15px",
					minWidth: "500px"
				}
			}} data-testid="orion-components-cbcomponents-new-video-player-components-main-menu-components-add-preset-dialog-dialog"
		>
			<DialogTitle sx={{ color: "#fff" }} data-testid="orion-components-cbcomponents-new-video-player-components-main-menu-components-add-preset-dialog-dialog-title">
				{getTranslation("global.CBComponents.newVideoPlayer.components.mainMenu.AddPresetDialog.title")}
			</DialogTitle>
			<DialogContent
				onKeyDown={event => {
					if (event.key === "Enter" && canSave) {
						handleSubmit();
					}
				}} data-testid="orion-components-cbcomponents-new-video-player-components-main-menu-components-add-preset-dialog-dialog-content"
			>
				<Typography sx={{ color: "#fff", mb: 2 }}>
					{getTranslation(
						"global.CBComponents.newVideoPlayer.components.mainMenu.AddPresetDialog.description"
					)}
				</Typography>
				{!isPresetValid ? (
					<Typography sx={{ color: "#FF0000", mb: 2, fontSize: 14 }}>
						{getTranslation(
							"global.CBComponents.newVideoPlayer.components.mainMenu.AddPresetDialog.invalidPreset"
						)}
					</Typography>
				) : (
					<div
						dir={dir}
						style={{
							display: "grid",
							gridTemplateColumns: "repeat(10, 1fr)",
							gap: 8
						}}
					>
						{presetSlots.map(slot => {
							const isSelected = selectedPresetValue === slot.value;
							const defaultTooltip = getTranslation(
								"global.CBComponents.newVideoPlayer.components.mainMenu.AddPresetDialog.existingPreset"
							);
							const tooltipTitle = slot.hasPreset
								? slot.name
									? getTranslation(
											"global.CBComponents.newVideoPlayer.components.mainMenu.AddPresetDialog.existingPresetWithName",
											null,
											slot.name
										)
									: defaultTooltip
								: "";

							const slotButton = (
								<button
									type="button"
									key={slot.value}
									disabled={isSaving}
									onClick={() => setSelectedPresetValue(slot.value)}
									style={{
										width: "100%",
										aspectRatio: "1 / 1",
										borderRadius: 6,
										border: isSelected ? "2px solid #FFFFFF" : "1px solid transparent",
										backgroundColor: slot.hasPreset ? "#4DB5F4" : "#70757D",
										color: "#fff",
										fontSize: 16,
										fontWeight: 500,
										cursor: isSaving ? "default" : "pointer",
										opacity: isSaving ? 0.7 : 1
									}} data-testid="orion-components-cbcomponents-new-video-player-components-main-menu-components-add-preset-dialog-button"
								>
									{slot.number}
								</button>
							);

							if (!slot.hasPreset) {
								return slotButton;
							}

							return (
								<Tooltip
									key={slot.value}
									title={tooltipTitle}
									placement="top"
									arrow
									disableInteractive
									PopperProps={{ disablePortal: true }} data-testid="orion-components-cbcomponents-new-video-player-components-main-menu-components-add-preset-dialog-tooltip"
								>
									<span style={{ width: "100%", display: "block" }}>{slotButton}</span>
								</Tooltip>
							);
						})}
					</div>
				)}
			</DialogContent>
			<DialogActions data-testid="orion-components-cbcomponents-new-video-player-components-main-menu-components-add-preset-dialog-dialog-actions">
				<Button onClick={handleDialogClose} disabled={isSaving} variant="text" style={{ color: "#B2B6BB" }} data-testid="orion-components-cbcomponents-new-video-player-components-main-menu-components-add-preset-dialog-button-2">
					{getTranslation("global.profiles.widgets.cameras.camCard.cancel")}
				</Button>
				<Button onClick={handleSubmit} disabled={!canSave} variant="contained" sx={{ color: "#fff" }} data-testid="orion-components-cbcomponents-new-video-player-components-main-menu-components-add-preset-dialog-button-3">
					{getTranslation("global.profiles.widgets.cameras.camCard.save")}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

AddPresetDialog.propTypes = {
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	onSave: PropTypes.func.isRequired,
	isSaving: PropTypes.bool,
	isPresetValid: PropTypes.bool,
	presets: PropTypes.arrayOf(
		PropTypes.shape({
			label: PropTypes.string,
			name: PropTypes.string,
			value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
		})
	),
	container: PropTypes.any
};

AddPresetDialog.defaultProps = {
	isSaving: false,
	isPresetValid: true,
	presets: [],
	container: null
};

export default AddPresetDialog;
